import type { ComponentProps, ReactNode } from 'react'
import type { Components, ExtraProps } from 'react-markdown'
import { twMerge } from 'tailwind-merge'
import { isAttachmentUrl } from '@highlight/common/product'
import { Markdown } from '@highlight/ui'
import { extractText, isElement, type HastElement, type HastNodes } from '@highlight/ui/markdown'
import { defaultSchema, type Options as SanitizeSchema } from 'rehype-sanitize'

import { DiffMarkerBox, HYPHENATED_SLOT_TAGS, diffMarkerWrapperClass, type DiffMarkerVariant } from './diff-marker'
import { QUOTE_HEADER_CLASS, QuoteHeaderContent } from './quote-header'

/**
 * The read-only counterparts of the Tiptap extensions the editor registers for staged-action
 * content (diff regions, quote headers, callout quotes, attachment links), as `components` and a
 * rehype plugin for `Markdown`. Raw HTML is parsed by rehype-raw, so these tags arrive as elements.
 */

const hasDescendant = (node: HastNodes, tags: Array<string>): boolean =>
  'children' in node && node.children.some((child) => isElement(child, ...tags) || hasDescendant(child, tags))

const ADDED_TAGS = ['added-content', 'content', 'insertion-point']
const REMOVED_TAGS = ['removed-content', 'deletion-point']
const QUOTE_HEADER_TAG = 'quote-header'

/**
 * The backend writes these tags with underscores, which CommonMark does not take for HTML (a tag
 * name has letters, digits and hyphens), so the parser would hand them back as text. Hyphenating
 * them is the same length, so source offsets still line up.
 */
export function hyphenateMarkerTags(source: string): string {
  return source.replace(
    /(<\/?)(added_content|removed_content|insertion_point|deletion_point|quote_header)(?=[\s/>])/g,
    (_, open: string, name: string) => open + name.replace('_', '-'),
  )
}

function createDiffMarker(variant: DiffMarkerVariant) {
  function DiffMarker({ node }: ExtraProps) {
    const label = extractText(node).trim()
    const hostsSlot = node
      ? isElement(node, ...HYPHENATED_SLOT_TAGS) || hasDescendant(node, HYPHENATED_SLOT_TAGS)
      : false
    return (
      <div className={diffMarkerWrapperClass}>
        <DiffMarkerBox variant={variant} label={label} hostsSlot={hostsSlot} />
      </div>
    )
  }
  DiffMarker.displayName = `StagedAction.${variant}Marker`
  return DiffMarker
}

function QuoteHeader({ node, ...props }: ComponentProps<'div'> & ExtraProps) {
  const quote = (props as Record<string, unknown>)['data-quote']
  return (
    <div className={QUOTE_HEADER_CLASS}>
      <QuoteHeaderContent
        label={extractText(node).trim()}
        resolveQuote={() => (typeof quote === 'string' && quote ? quote : null)}
      />
    </div>
  )
}

/** The editor swaps the blockquote for a callout box. */
function CalloutQuote({ node: _node, className, children, ...props }: ComponentProps<'blockquote'> & ExtraProps) {
  return (
    <blockquote
      className={twMerge('bg-element text-foreground corner-smooth rounded-lg border-0 p-3', className)}
      {...props}
    >
      {children}
    </blockquote>
  )
}

const BLOCK_MARKERS = [...ADDED_TAGS, ...REMOVED_TAGS, QUOTE_HEADER_TAG]

/** A marker on a line of its own can still land inside a paragraph; a block in a `<p>` is invalid DOM. */
function Paragraph(props: ComponentProps<'p'> & ExtraProps) {
  const content = props.node?.children.filter((child) => child.type !== 'text' || child.value.trim())
  if (content?.length === 1 && isElement(content[0], ...BLOCK_MARKERS)) return <>{props.children}</>
  return <Markdown.Paragraph {...props} />
}

/** `attachment:` pseudo-URLs render as plain spans (no broken navigation); the rest are external links. */
function AttachmentAwareLink({ node: _node, href, children, ...props }: ComponentProps<'a'> & ExtraProps) {
  if (isAttachmentUrl(href)) return <span {...props}>{children}</span>
  return (
    <a href={href} target="_blank" rel="noopener noreferrer nofollow" {...props}>
      {children}
    </a>
  )
}

// Marker tag names are outside react-markdown's intrinsic-element typing, so they are keyed loosely.
const MARKER_COMPONENTS: Record<string, (props: ExtraProps) => ReactNode> = {
  ...Object.fromEntries(ADDED_TAGS.map((tag) => [tag, createDiffMarker('added')])),
  ...Object.fromEntries(REMOVED_TAGS.map((tag) => [tag, createDiffMarker('removed')])),
  [QUOTE_HEADER_TAG]: QuoteHeader,
}

export const stagedActionComponents: Components = {
  ...MARKER_COMPONENTS,
  p: Paragraph,
  a: AttachmentAwareLink,
  blockquote: CalloutQuote,
}

const withAttributes = (tag: string, ...extra: Array<string>) => [...(defaultSchema.attributes?.[tag] ?? []), ...extra]

/**
 * What rehype-sanitize keeps after rehype-raw parses model-written content: GitHub's defaults, the
 * marker tags, the `label`, `data-checked`, `aria-label` and `data-col` that `Markdown`'s own task
 * item and table plugins add before this runs, and `attachment:` links and images.
 */
export const stagedActionSanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  strip: [...(defaultSchema.strip ?? []), 'style'],
  tagNames: [...(defaultSchema.tagNames ?? []), ...BLOCK_MARKERS, 'label'],
  attributes: {
    ...defaultSchema.attributes,
    input: withAttributes('input', 'ariaLabel'),
    li: withAttributes('li', 'dataChecked'),
    td: withAttributes('td', 'dataCol'),
    th: withAttributes('th', 'dataCol'),
  },
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), 'attachment'],
    src: [...(defaultSchema.protocols?.src ?? []), 'attachment'],
  },
}

/** Markdown of a blockquote without its `> ` prefixes, from the source when the tree kept offsets. */
function quoteText(source: string, quote: HastElement): string {
  const start = quote.position?.start.offset
  const end = quote.position?.end.offset
  if (start === undefined || end === undefined) return extractText(quote).trim()
  return source
    .slice(start, end)
    .replace(/^[ \t]{0,3}>[ \t]?/gm, '')
    .trim()
}

/** The header may be a block of its own or the only thing in a paragraph. */
function headerAt(node: HastNodes): HastElement | undefined {
  if (node.type !== 'element') return undefined
  if (node.tagName === QUOTE_HEADER_TAG) return node
  if (node.tagName !== 'p') return undefined
  const content = node.children.filter((child) => child.type !== 'text' || child.value.trim())
  const [only] = content
  return content.length === 1 && isElement(only, QUOTE_HEADER_TAG) ? only : undefined
}

const isBlank = (node: HastNodes): boolean =>
  (node.type === 'text' && !node.value.trim()) || (isElement(node, 'p') && !extractText(node).trim())

/**
 * Pairs each quote header with the blockquote that follows it (past blank lines), the same
 * positional pairing the editor resolves at click time, and hands the quote's markdown to the
 * header as `data-quote` so its copy button needs nothing but the DOM it sits in.
 */
export function rehypeQuoteSource(source: string) {
  function visit(parent: HastNodes) {
    if (!('children' in parent)) return
    const siblings = parent.children
    siblings.forEach((child, index) => {
      const header = headerAt(child)
      if (header) {
        const quote = siblings.slice(index + 1).find((next) => !isBlank(next))
        if (quote && isElement(quote, 'blockquote')) {
          header.properties = { ...header.properties, dataQuote: quoteText(source, quote) }
        }
      }
      visit(child)
    })
  }
  return () => visit
}
